import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Search, CheckCircle, XCircle, AlertTriangle, Clock, Building2, MapPin, Calendar, Briefcase, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { PublicLayout } from "../components/PublicLayout";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificationPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      toast.error("Veuillez entrer un numéro d'habilitation");
      return;
    }

    setLoading(true);
    setResult(null);
    setNotFound(false);

    try {
      const response = await axios.get(`${API_URL}/public/verify-habilitation`, {
        params: { id_igt: searchTerm.trim() }
      });
      setResult(response.data.cabinet);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors de la vérification");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      actif: {
        icon: CheckCircle,
        label: "Actif",
        bgColor: "bg-emerald-50",
        borderColor: "border-emerald-200",
        textColor: "text-emerald-700",
        iconColor: "text-emerald-600",
        description: "Ce cabinet est habilité et autorisé à exercer"
      },
      pre_autorise: {
        icon: Clock,
        label: "En cours de validation",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
        textColor: "text-blue-700",
        iconColor: "text-blue-600",
        description: "Dossier en cours de validation par l'IGT"
      },
      suspendu: {
        icon: AlertTriangle,
        label: "Suspendu",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
        textColor: "text-amber-700",
        iconColor: "text-amber-600",
        description: "Habilitation temporairement suspendue"
      },
      revoque: {
        icon: XCircle,
        label: "Révoqué",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        textColor: "text-red-700",
        iconColor: "text-red-600",
        description: "Habilitation révoquée - Non autorisé à exercer"
      }
    };
    return configs[status] || configs.suspendu;
  };

  return (
    <PublicLayout>
        {/* Titre de la page */}
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
            Vérification d'Habilitation
          </h2>
          <p className="text-slate-600">
            Vérifiez le statut d'habilitation d'un cabinet agréé par l'IGT
          </p>
        </div>

        {/* Formulaire de recherche */}
        <Card className="mb-8 shadow-lg border-0">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-[#1B4D3E]" />
              Rechercher un cabinet
            </CardTitle>
            <CardDescription>
              Entrez le numéro d'habilitation pour vérifier le statut du cabinet
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Ex: 0126BS, IGT-2025-001..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                  className="text-lg h-12 font-mono"
                  data-testid="habilitation-search-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading}
                className="h-12 px-8 bg-[#1B4D3E] hover:bg-[#2d7a62]"
                data-testid="habilitation-search-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Vérifier
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Résultat - Cabinet trouvé */}
        {result && (
          <Card className="shadow-lg border-0 overflow-hidden" data-testid="verification-result">
            <div className={`p-4 ${getStatusConfig(result.status).bgColor} border-b ${getStatusConfig(result.status).borderColor}`}>
              <div className="flex items-center gap-3">
                {(() => {
                  const StatusIcon = getStatusConfig(result.status).icon;
                  return <StatusIcon className={`w-8 h-8 ${getStatusConfig(result.status).iconColor}`} />;
                })()}
                <div>
                  <p className={`text-lg font-bold ${getStatusConfig(result.status).textColor}`}>
                    {getStatusConfig(result.status).label}
                  </p>
                  <p className={`text-sm ${getStatusConfig(result.status).textColor} opacity-80`}>
                    {getStatusConfig(result.status).description}
                  </p>
                </div>
              </div>
            </div>
            
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Nom du cabinet */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Building2 className="w-5 h-5 text-slate-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Nom du Cabinet</p>
                    <p className="text-lg font-semibold text-slate-800">{result.nom}</p>
                  </div>
                </div>

                {/* Numéro d'habilitation */}
                <div className="flex items-start gap-3 p-4 bg-[#1B4D3E]/5 rounded-lg border border-[#1B4D3E]/20">
                  <img src="/logo_metps.jpg" alt="IGT" className="w-8 h-8 rounded-full object-contain bg-white p-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Numéro d'Habilitation IGT</p>
                    <p className="text-xl font-mono font-bold text-[#1B4D3E]">{result.id_igt}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Région */}
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Région</p>
                      <p className="font-medium text-slate-800">{result.region || "Non spécifiée"}</p>
                    </div>
                  </div>

                  {/* Date d'habilitation */}
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Date d'Habilitation</p>
                      <p className="font-medium text-slate-800">{result.habilitation_date || "Non disponible"}</p>
                    </div>
                  </div>
                </div>

                {/* Secteur d'activité */}
                {result.secteur_activite && (
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                    <Briefcase className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Secteur d'Activité</p>
                      <p className="font-medium text-slate-800">{result.secteur_activite}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Résultat - Non trouvé */}
        {notFound && (
          <Card className="shadow-lg border-0 border-l-4 border-l-red-500" data-testid="verification-not-found">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Aucun cabinet trouvé</p>
                  <p className="text-sm text-slate-600">
                    Le numéro d'habilitation "<span className="font-mono font-bold">{searchTerm}</span>" ne correspond à aucun cabinet enregistré.
                  </p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Conseils :</strong> Vérifiez l'orthographe du numéro ou contactez l'IGT pour plus d'informations.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Nouvelle recherche */}
        {(result || notFound) && (
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => {
                setResult(null);
                setNotFound(false);
                setSearchTerm("");
              }}
            >
              Nouvelle recherche
            </Button>
          </div>
        )}

        {/* Lien vers connexion */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 mb-2">Vous êtes un cabinet habilité ?</p>
          <Link to="/login">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Accéder à l'espace cabinet
            </Button>
          </Link>
        </div>
    </PublicLayout>
  );
}
